import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import {
  fetchUserDocuments,
  getMissingStatements,
  markStatementMonthAsNotExpected,
} from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { NoLBAIntroPage } from '@/features/onboarding/upload-statements-stage/NoLBAIntroPage'
import {
  NotExpectedState,
  NotExpectedStateAction,
} from '@/features/onboarding/upload-statements-stage/types'
import { OnboardingTaskIdents } from '@/features/onboarding/config'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UploadPrevMonthStatementStatus } from '@/features/user/dashboard/lib/userActionItemStatuses'
import { useCompleteOnboardingStepIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.hooks'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { useAnalyticsTrack } from '@/features/amplitude'
import { selectUserStatements } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { isoToUTCDateTime, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { Tooltip } from '@/features/taxes/quarterly/components/Tooltip'
import { Button, Image, Loader, Separator } from '@/ui'

export const StatementsUploadPage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const [loading, setLoading] = useState(true)

  const [notExpectedState, setNotExpectedValue] = useReducer(
    (
      state: NotExpectedState,
      { financialAccountId, monthYear, value }: NotExpectedStateAction
    ) => ({
      ...state,
      [financialAccountId]: {
        ...(state[financialAccountId] || {}),
        [monthYear]: value,
      },
    }),
    {}
  )

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        dispatch(fetchFinancialAccountsIfNeeded()),
        dispatch(fetchPlaidItemsIfNeeded()),
        dispatch(fetchUserDocuments()),
      ])
      setLoading(false)
    }
    init()
  }, [dispatch])

  const plaidItems = useReselector(selectPlaidItems)
  const statements = useReselector(selectUserStatements)
  const missingStatements = useFetchResponse(getMissingStatements)

  const allMonthsCovered = useMemo(() => {
    if (!missingStatements?.length) return false

    return missingStatements.every((account) => {
      const accountId = account.financialAccountId
      if (!accountId) return true

      return account.statements.every((isoDate) => {
        const yearMonth = isoToUTCDateTime(isoDate).toFormat(
          DATE_FORMATS_LUXON.YEAR_MONTH
        )

        const hasUpload = statements.some(
          (s) =>
            s.statementMonth === yearMonth &&
            s.statement?.statementFinancialAccounts?.some(
              (a) => a.financialAccountId === accountId
            )
        )

        const markedNotExpected = Boolean(
          notExpectedState[accountId]?.[yearMonth]
        )

        return hasUpload || markedNotExpected
      })
    })
  }, [missingStatements, statements, notExpectedState])

  const { markComplete: completeBankStatementItem } =
    useCompleteOnboardingStepIfNeeded(
      GettingStartedOnboardingStep.uploadBankStatements
    )

  const handleComplete = useCallback(async () => {
    await completeBankStatementItem()
    await markUserActionItemCompleteIfExists(
      OnboardingTaskIdents.UPLOAD_PREV_MONTH_STATEMENTS,
      undefined,
      UploadPrevMonthStatementStatus.uploaded
    )
    Object.entries(notExpectedState).forEach(
      ([financialAccountId, monthYearValues]) => {
        Object.entries(monthYearValues).forEach(async ([monthYear, value]) => {
          await markStatementMonthAsNotExpected({
            financialAccountId: Number(financialAccountId),
            statementMonth: monthYear,
            notExpected: value,
          })(dispatch)
        })
      }
    )
    track('completed upload monthly bank statements page')
    navigate('/dashboard')
  }, [completeBankStatementItem, notExpectedState, dispatch, track, navigate])

  if (loading) {
    return <Loader />
  }

  const hasConnectedAccounts = Object.keys(plaidItems).length > 0

  if (!hasConnectedAccounts) {
    return <Navigate to="/practice/documents?tab=bank-statements" replace />
  }

  return (
    <div className="mx-auto max-w-2xl py-10 px-4">
      <Image
        src="https://heard-images.s3.amazonaws.com/assets/upload-document.svg"
        alt="Upload document"
        width={80}
        height={80}
        objectFit="scaleDown"
        className="mb-4"
      />
      <NoLBAIntroPage
        setShowSkipButton={() => {}}
        notExpectedState={notExpectedState}
        setNotExpectedValue={setNotExpectedValue}
      />

      <Separator className="mt-6" />
      <div className="flex items-center justify-between py-6">
        <Button variant="link" onClick={() => navigate('/dashboard')}>
          Back
        </Button>
        {!allMonthsCovered ? (
          <Tooltip
            popup={{
              body: 'All months must have a statement uploaded or be marked as not expected',
            }}
            labelComponent={
              <Button disabled size="lg">
                Complete
              </Button>
            }
          />
        ) : (
          <Button size="lg" onClick={handleComplete}>
            Complete
          </Button>
        )}
      </div>
    </div>
  )
}
